import { BellOutlined, MenuFoldOutlined, MenuUnfoldOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Badge, Breadcrumb, Button, Input, Layout, Menu, Space, Tag, Tooltip, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useState, type PropsWithChildren } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { appRoutes, routeGroups } from '../routes';
import { canAccessRoute } from '../components/RoutePermissionBoundary';
import { authStore, useAdminAuthState } from '../stores/auth';
import { EnterpriseContextHeader, enterpriseContextPath } from '../views/enterprise/EnterpriseContextHeader';
import { LocaleSwitcher } from '../components/LocaleSwitcher';

const { Header, Sider, Content } = Layout;
const roleLabels: Record<string, string> = { SUPER_ADMIN: '超级管理员', ADMIN: '平台管理员' };
const enterpriseMenuSections = [
  { key: 'overview', label: '经营总览' },
  { key: 'customers', label: '客户与准入' },
  { key: 'delivery', label: '资源与履约' },
  { key: 'service', label: '服务与渠道' },
  { key: 'finance-risk', label: '财务与风控' },
] as const;

export function AppLayout({ children }: PropsWithChildren) {
  const [collapsed, setCollapsed] = useState(false);
  const { access } = useAdminAuthState();
  const navigate = useNavigate();
  const location = useLocation();
  const visibleRoutes = appRoutes.filter((route) => !route.public && canAccessRoute(route));
  const currentRoute = appRoutes.find((route) => matchPath({ path: route.path, end: true }, location.pathname));
  const enterpriseDetailMatch = matchPath('/enterprise/organizations/:id', location.pathname);
  const currentOrganizationId = new URLSearchParams(location.search).get('organizationId')
    ?? enterpriseDetailMatch?.params.id
    ?? undefined;
  const menuItems: MenuProps['items'] = routeGroups.map((group) => {
    const routes = visibleRoutes.filter((route) => route.group === group.key && !route.hideInMenu);
    const children: MenuProps['items'] = group.key === 'enterprise'
      ? enterpriseMenuSections.map((section) => ({
        key: `enterprise-section:${section.key}`,
        type: 'group' as const,
        label: section.label,
        children: routes.filter((route) => route.enterpriseSection === section.key)
          .map((route) => ({ key: route.key, icon: route.icon, label: route.label })),
      })).filter((section) => section.children.length)
      : routes.map((route) => ({ key: route.key, icon: route.icon, label: route.label }));
    return { key: `group:${group.key}`, icon: group.icon, label: group.label, children };
  }).filter((group) => Boolean(group && 'children' in group && group.children?.length));
  const dataScopeSummary = access?.dataScopes.length
    ? `已启用 ${access.dataScopes.length} 项数据范围约束`
    : '未返回数据范围';
  const navigateToRoute = (key: string) => {
    const route = appRoutes.find((item) => item.key === key);
    if (!route) return;
    navigate(route.group === 'enterprise'
      ? enterpriseContextPath(route.path, currentOrganizationId)
      : route.path);
  };

  return <Layout className="app-shell">
    <Sider theme="light" breakpoint="lg" collapsedWidth={0} collapsible collapsed={collapsed} trigger={null} width={252} className="app-sider">
      <div className="brand"><div className="brand-mark">F</div>{!collapsed && <div><Typography.Text className="brand-name">FlashMate</Typography.Text><Typography.Text className="brand-caption">运营管理中心</Typography.Text></div>}</div>
      <Menu mode="inline" selectedKeys={[currentRoute?.key ?? 'dashboard']} defaultOpenKeys={routeGroups.map((group) => `group:${group.key}`)} items={menuItems} onClick={({ key }: { key: string }) => navigateToRoute(key)} />
      {!collapsed && <div className="sider-footer"><span className="status-dot" />服务运行正常</div>}
    </Sider>
    <Layout>
      <Header className="app-header">
        <Space size={16}><Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setCollapsed((value) => !value)} /><Input variant="borderless" prefix={<SearchOutlined />} placeholder="搜索功能、用户或订单" className="global-search" /></Space>
        <Space size={16} className="header-actions">
          <LocaleSwitcher />
          <Tooltip title={dataScopeSummary}><Tag color={access?.dataScopes.length ? 'blue' : 'default'}>数据范围 {access?.dataScopes.length ?? 0}</Tag></Tooltip>
          <Badge dot><BellOutlined className="header-icon" /></Badge>
          <div className="account">
            <Avatar size={32} icon={<UserOutlined />} />
            <span>{access?.displayName || '管理员'}</span>
            <Typography.Text type="secondary">{roleLabels[access?.role ?? ''] ?? access?.role}</Typography.Text>
            <Button type="link" onClick={() => { authStore.signOut(); navigate('/login', { replace: true }); }}>退出</Button>
          </div>
        </Space>
      </Header>
      <Content className="app-content"><div className="content-inner"><Breadcrumb className="app-breadcrumb" items={[{ title: 'FlashMate' }, ...(currentRoute?.group ? [{ title: routeGroups.find((group) => group.key === currentRoute.group)?.label }] : []), ...(currentRoute ? [{ title: currentRoute.label }] : [])]} />{currentRoute?.group === 'enterprise' ? <EnterpriseContextHeader /> : null}{children}</div></Content>
    </Layout>
  </Layout>;
}
